import { bitswap } from '@helia/block-brokers';
import { unixfs } from '@helia/unixfs';
import { MemoryBlockstore } from 'blockstore-core';
import { IDBBlockstore } from 'blockstore-idb';
import { IDBDatastore } from 'datastore-idb';
import { createHelia } from 'helia';

import { HeliaLifecycleService } from '@/services/abstract/poc/HeliaLifecycleService';
import { P2P_CONFIG } from '@/config/p2pConfig';
import type { LoggerService } from '@/services/abstract/LoggerService';
import type { Logger } from '@/types/Logger';
import type { HeliaNode, HeliaNodeOptions } from '@/types/poc/helia';
import type { LibP2pWithServices } from '@/types/poc/libp2p';

const BLOCKSTORE_NAME = 'helia-blockstore';
const DATASTORE_NAME = 'helia-datastore';

interface IDBStoreRefs {
  blockstore: IDBBlockstore;
  datastore: IDBDatastore;
}

export class HeliaLifecycleServiceImpl extends HeliaLifecycleService {
  private readonly log: Logger;
  private readonly nodeCache = new Map<string, HeliaNode>();
  private readonly storeCache = new Map<string, IDBStoreRefs>();
  private readonly defaultOptions: HeliaNodeOptions;

  constructor(loggerService: LoggerService, defaultOptions: HeliaNodeOptions = {}) {
    super();
    this.log = loggerService.createLogger({
      namespace: P2P_CONFIG.logging.namespace.helia,
    });
    this.defaultOptions = defaultOptions;
  }

  async createNode(
    libp2p: LibP2pWithServices,
    options?: HeliaNodeOptions
  ): Promise<HeliaNode> {
    const nodeOptions = { ...this.defaultOptions, ...options };
    const cacheKey = HeliaLifecycleServiceImpl.generateNodeCacheKey(libp2p, nodeOptions);

    const cachedNode = this.nodeCache.get(cacheKey);
    if (cachedNode != null) {
      return cachedNode;
    }

    const node =
      nodeOptions.useMemory ?? false
        ? await HeliaLifecycleServiceImpl.createMemoryNode(libp2p)
        : await this.createPersistentNode(libp2p, nodeOptions, cacheKey);

    this.log.debug('Registered protocols:', node.helia.libp2p.getProtocols());
    this.nodeCache.set(cacheKey, node);

    return node;
  }

  async destroyNode(node: HeliaNode): Promise<void> {
    try {
      await node.helia.stop();

      for (const [key, cachedNode] of this.nodeCache.entries()) {
        if (cachedNode !== node) {
          continue;
        }

        this.nodeCache.delete(key);
        const stores = this.storeCache.get(key);
        if (stores != null) {
          this.log.debug(`Closing IDB stores for node: ${key}`);
          await Promise.all([stores.blockstore.close(), stores.datastore.close()]);
          this.storeCache.delete(key);
          this.log.debug('IDB stores closed');
        }
        break;
      }
    } catch (error) {
      throw new Error(
        `Failed to destroy Helia node: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  private async createPersistentNode(
    libp2p: LibP2pWithServices,
    options: HeliaNodeOptions,
    cacheKey: string
  ): Promise<HeliaNode> {
    try {
      const blockstoreName = options.blockstoreName?.trim() ?? BLOCKSTORE_NAME;
      const datastoreName = options.datastoreName?.trim() ?? DATASTORE_NAME;

      const blockstore = new IDBBlockstore(blockstoreName);
      const datastore = new IDBDatastore(datastoreName);

      await Promise.all([blockstore.open(), datastore.open()]);
      this.storeCache.set(cacheKey, { blockstore, datastore });

      const helia = await createHelia({
        libp2p,
        blockstore,
        datastore,
        blockBrokers: [bitswap(HeliaLifecycleServiceImpl.createBitswapConfig())],
      });
      const fs = unixfs(helia);

      return { helia, fs };
    } catch (error) {
      this.storeCache.delete(cacheKey);
      throw new Error(
        `Failed to create persistent Helia node: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  private static async createMemoryNode(libp2p: LibP2pWithServices): Promise<HeliaNode> {
    try {
      const blockstore = new MemoryBlockstore();
      const helia = await createHelia({
        libp2p,
        blockstore,
        blockBrokers: [bitswap(HeliaLifecycleServiceImpl.createBitswapConfig())],
      });
      const fs = unixfs(helia);

      return { helia, fs };
    } catch (error) {
      throw new Error(
        `Failed to create memory Helia node: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  private static createBitswapConfig() {
    return {
      maxInboundStreams: P2P_CONFIG.bitswap.maxInboundStreams,
      maxOutboundStreams: P2P_CONFIG.bitswap.maxOutboundStreams,
      messageSendConcurrency: P2P_CONFIG.bitswap.messageSendConcurrency,
      sendBlocksConcurrency: P2P_CONFIG.bitswap.sendBlocksConcurrency,
      sendBlocksDebounce: P2P_CONFIG.bitswap.sendBlocksDebounce,
      incomingStreamTimeout: P2P_CONFIG.bitswap.incomingStreamTimeout,
      outgoingStreamTimeout: P2P_CONFIG.bitswap.outgoingStreamTimeout,
      sendBlocksTimeout: P2P_CONFIG.bitswap.sendBlocksTimeout,
      receiveBlocksTimeout: P2P_CONFIG.bitswap.receiveBlocksTimeout,
      streamIdleTimeout: P2P_CONFIG.bitswap.streamIdleTimeout,
    };
  }

  private static generateNodeCacheKey(
    libp2p: LibP2pWithServices,
    options: HeliaNodeOptions
  ): string {
    return `${libp2p.peerId.toString()}-${JSON.stringify(options)}`;
  }
}
